import React, { useState, useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import { cn } from '@/lib/utils'
import { useTheme } from '@/contexts/ThemeContext'
import { ForumTopic } from '@/types/forum'
import { parseLinks } from '@/lib/utils/parseLinks'
import { ChevronDown } from 'lucide-react'
import { supabase } from '@/lib/supabase'
import type { Member } from '@/types/user'

interface TopicContentProps {
  topic: ForumTopic
}

const COLLAPSE_THRESHOLD = 500

const TopicContent: React.FC<TopicContentProps> = ({ topic }) => {
  const { isDarkMode } = useTheme()
  const [isExpanded, setIsExpanded] = useState(false)
  const [shouldCollapse, setShouldCollapse] = useState(false)
  const contentRef = useRef<HTMLDivElement>(null)
  const [allUsers, setAllUsers] = useState<Member[]>([])

  useEffect(() => {
    supabase
      .from('users')
      .select('id, auth_id, full_name')
      .then(({ data }) => data && setAllUsers(data))
  }, [])

  useEffect(() => {
    if (topic.content.length > COLLAPSE_THRESHOLD) {
      setShouldCollapse(true)
    }
  }, [topic.content])

  const formatPlain = (content: string) =>
    content.split('\n').map((line, i) => (
      <React.Fragment key={i}>
        {parseLinks(line)}
        <br />
      </React.Fragment>
    ))

  const renderContent = (content: string) => {
    if (!allUsers.length) {
      return formatPlain(content)
    }
    return content.split(/(\s+|\n)/).map((part, i) => {
      if (part.startsWith('@')) {
        const name = part.slice(1).trim()
        const u = allUsers.find(u => u.full_name === name)
        if (u) {
          return (
            <Link key={i} to={`/dashboard/members/${u.id}`} className="text-gold-400">
              @{name}
            </Link>
          )
        }
      }
      if (part === '\n') return <br key={i} />
      return <React.Fragment key={i}>{parseLinks(part)}</React.Fragment>
    })
  }

  const contentIncludesMedia =
    topic.media &&
    new RegExp(topic.media.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).test(topic.content)

  return (
    <div
      className={cn(
        'px-6 pt-2 pb-6 relative overflow-hidden border-b',
        isDarkMode ? 'border-dark-300' : 'border-light-300'
      )}
    >
      <div
        className={cn(
          'prose max-w-none',
          isDarkMode
            ? 'prose-invert text-white font-medium drop-shadow-sm'
            : 'text-light-900 font-medium'
        )}
      >
        <div
          ref={contentRef}
          className={cn(
            'whitespace-pre-line',
            shouldCollapse && !isExpanded && 'max-h-[300px] overflow-hidden relative'
          )}
        >
          <div className="whitespace-pre-wrap">{renderContent(topic.content)}</div>

          {/* Display media only if not already embedded in content */}
          {topic.media && !contentIncludesMedia && (
            <div className="mt-4">
              <img
                src={topic.media}
                alt="Topic attachment"
                className="max-h-96 rounded-lg mx-auto"
                loading="lazy"
              />
            </div>
          )}

          {/* Display video always if present */}
          {topic.video && (
            <div className="mt-4">
              <video
                src={topic.video}
                controls
                className="max-h-96 w-full rounded-lg"
                preload="metadata"
              />
            </div>
          )}

          {shouldCollapse && !isExpanded && (
            <div
              className={cn(
                'absolute bottom-0 left-0 right-0 h-24 pointer-events-none bg-gradient-to-t',
                isDarkMode ? 'from-dark-300/95 to-transparent' : 'from-light-200/95 to-transparent'
              )}
            />
          )}
        </div>

        {shouldCollapse && (
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className={cn(
              'flex items-center justify-center w-full mt-4 py-2 px-4 rounded-lg text-sm font-medium transition-colors border',
              isDarkMode
                ? 'bg-dark-300 hover:bg-dark-400 text-white border-dark-400'
                : 'bg-light-200 hover:bg-light-300 text-light-900 border-light-300'
            )}
          >
            <span>{isExpanded ? 'Show less' : 'Show more'}</span>
            <ChevronDown
              className={cn('ml-2 h-4 w-4 transition-transform', isExpanded && 'rotate-180')}
            />
          </button>
        )}
      </div>
    </div>
  )
}

export default TopicContent
